// src/components/opportunity/OpportunityCreateModal.tsx
//
// Lightweight creation form. Phase 1 doesn't try to surface the full
// lead-link picker yet — `lead_id` is an optional pasted UUID for now;
// the polish slice that fuses Lead → Opportunity in one click lands
// once the lead-side handoff button is added. The form only requires
// one of {project_name, customer_name, customer_company} (the server
// enforces the same).

import { useEffect, useMemo, useState } from "react";
import {
  OPPORTUNITY_PRODUCT_TYPE_OTHER,
  type OpportunityProductType,
} from "@/types/opportunity";
import { createOpportunity } from "@/lib/opportunity/opportunityClient";
import { useDualReadProductTypes } from "@/hooks/useDualReadProductTypes";
import {
  buildOpportunityProductTypeOptions,
  formatOpportunityProductTypeOptionLabel,
} from "@/lib/opportunity/productTypeLabel";
import { listEffectiveServiceOfferings } from "@/lib/service/client";
import type { EffectiveServiceOffering } from "@/lib/service/types";
import { AddressAutocompleteInput } from "@/components/common/AddressAutocompleteInput";

export type OpportunityCreateModalProps = {
  open: boolean;
  onClose: () => void;
  onCreated: (opportunityId: string) => void;
};

export function OpportunityCreateModal({ open, onClose, onCreated }: OpportunityCreateModalProps) {
  const productTypes = useDualReadProductTypes();
  const [inheritedOfferings, setInheritedOfferings] = useState<EffectiveServiceOffering[]>([]);

  useEffect(() => {
    if (!open) return;
    void listEffectiveServiceOfferings({ quotable_only: true }).then((res) => {
      if (!res.ok) return;
      setInheritedOfferings(res.data.offerings.filter((o) => o.is_inherited));
    });
  }, [open, productTypes]);

  const productTypeOptions = useMemo(
    () => buildOpportunityProductTypeOptions(productTypes, inheritedOfferings),
    [productTypes, inheritedOfferings],
  );
  const [productType, setProductType] = useState<OpportunityProductType>(
    () => productTypeOptions[0]?.value ?? OPPORTUNITY_PRODUCT_TYPE_OTHER,
  );
  // If Deal Types load or change after mount and the current selection
  // drops out of the list, snap to the first available option.
  useEffect(() => {
    if (!productTypeOptions.some((o) => o.value === productType)) {
      setProductType(productTypeOptions[0]?.value ?? OPPORTUNITY_PRODUCT_TYPE_OTHER);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productTypeOptions]);
  const [projectName, setProjectName] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerCompany, setCustomerCompany] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [leadId, setLeadId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  function reset() {
    setProductType(productTypeOptions[0]?.value ?? OPPORTUNITY_PRODUCT_TYPE_OTHER);
    setProjectName("");
    setCustomerName("");
    setCustomerCompany("");
    setCustomerPhone("");
    setCustomerEmail("");
    setCustomerAddress("");
    setLeadId("");
    setError(null);
  }

  async function submit() {
    setError(null);
    if (!projectName.trim() && !customerName.trim() && !customerCompany.trim()) {
      setError("Add at least a project name, customer name, or company name.");
      return;
    }
    setBusy(true);
    const r = await createOpportunity({
      product_type: productType,
      project_name: projectName.trim() || undefined,
      customer_name: customerName.trim() || undefined,
      customer_company: customerCompany.trim() || undefined,
      customer_phone: customerPhone.trim() || undefined,
      customer_email: customerEmail.trim() || undefined,
      customer_address: customerAddress.trim() || undefined,
      lead_id: leadId.trim() || null,
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    onCreated(r.data.opportunity.id);
    reset();
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-xl rounded-2xl bg-white p-5 shadow-xl dark:bg-zinc-900">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-zinc-100">New opportunity</h3>
            <p className="text-xs text-gray-500 dark:text-zinc-400 mt-0.5">
              Capture the basics — pricing, documents, and revisions land inside the
              workspace.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              reset();
              onClose();
            }}
            className="text-xs text-gray-500 hover:text-gray-700 dark:text-zinc-400 dark:hover:text-zinc-200"
          >
            ✕
          </button>
        </div>

        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 mb-3 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
            {error}
          </div>
        ) : null}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Product type">
            <select
              value={productType}
              onChange={(e) => setProductType(e.target.value)}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
              disabled={busy}
            >
              {productTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {formatOpportunityProductTypeOptionLabel(opt)}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">
              Products come from Ops → Deal Types, with Admin Org Settings as fallback. Inherited
              services from manufacturers show with the publisher name in parentheses.
            </p>
          </Field>

          <Field label="Project name">
            <input
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="e.g. Smith Barn — Hwy 64"
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
              disabled={busy}
            />
          </Field>

          <Field label="Customer name">
            <input
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="John Smith"
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
              disabled={busy}
            />
          </Field>

          <Field label="Company">
            <input
              value={customerCompany}
              onChange={(e) => setCustomerCompany(e.target.value)}
              placeholder="Smith Construction LLC"
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
              disabled={busy}
            />
          </Field>

          <Field label="Phone">
            <input
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              placeholder="(555) 123-4567"
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
              disabled={busy}
            />
          </Field>

          <Field label="Email">
            <input
              type="email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              placeholder="customer@example.com"
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
              disabled={busy}
            />
          </Field>

          <Field label="Address" wide>
            <AddressAutocompleteInput
              value={customerAddress}
              onChange={setCustomerAddress}
              disabled={busy}
              placeholder="123 Main St, Asheville NC"
            />
          </Field>

          <Field label="Lead ID (optional)" wide>
            <input
              value={leadId}
              onChange={(e) => setLeadId(e.target.value)}
              placeholder="Paste a lead UUID to link this opportunity"
              className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 font-mono dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
              disabled={busy}
            />
            <p className="text-xs text-gray-400 mt-1">
              The polish slice that adds a "promote lead → opportunity" button will
              replace this manual paste.
            </p>
          </Field>
        </div>

        <div className="flex items-center justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={busy}
            className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy}
            className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50"
          >
            {busy ? "Creating…" : "Create opportunity"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field(props: { label: string; wide?: boolean; children: React.ReactNode }) {
  return (
    <div className={`space-y-1 ${props.wide ? "sm:col-span-2" : ""}`}>
      <label className="text-xs font-medium text-gray-600 dark:text-zinc-300">{props.label}</label>
      {props.children}
    </div>
  );
}
